import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, User, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";

import { API_URL, api } from "@/lib/api";

/**
 * Floating chatbot widget — Kai, powered by Claude Sonnet 4.5.
 * Sits fixed bottom-right on the public landing page.
 * Streams responses via SSE from /api/chat/message.
 * Shows an inline lead-capture form when the visitor volunteers an email
 * (or clicks the "Share my details" button) — collects Name, Email, Phone
 * + Country Code and Country before posting to /api/chat/capture.
 */
const SESSION_KEY = "kairosa_chat_session";
const EMAIL_RE = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;

const DIAL_CODES = [
  ["+91", "India"], ["+1", "USA / Canada"], ["+44", "UK"], ["+61", "Australia"],
  ["+971", "UAE"], ["+65", "Singapore"], ["+49", "Germany"], ["+33", "France"],
  ["+81", "Japan"], ["+86", "China"], ["+82", "Korea"], ["+64", "New Zealand"],
  ["+27", "South Africa"], ["+55", "Brazil"], ["+52", "Mexico"], ["+34", "Spain"],
  ["+39", "Italy"], ["+31", "Netherlands"], ["+41", "Switzerland"], ["+46", "Sweden"],
  ["+45", "Denmark"], ["+47", "Norway"], ["+90", "Turkey"], ["+62", "Indonesia"],
  ["+63", "Philippines"], ["+66", "Thailand"], ["+60", "Malaysia"], ["+92", "Pakistan"],
  ["+880", "Bangladesh"], ["+94", "Sri Lanka"], ["+977", "Nepal"], ["+20", "Egypt"],
  ["+254", "Kenya"], ["+234", "Nigeria"], ["+966", "Saudi Arabia"], ["+972", "Israel"],
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm Kai — the on-site guide for Kairosa. Ask me about plans, modules, or how it can run your studio, café or retreat.",
    },
  ]);
  const [input, setInput] = useState("");
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [capBusy, setCapBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", country_code: "+91", phone: "", country: "" });
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(null);

  useEffect(() => {
    try { sessionIdRef.current = localStorage.getItem(SESSION_KEY); } catch {}
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, captureOpen]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setBusy(true);

    // Pre-fill capture form from any info the visitor typed
    const em = text.match(EMAIL_RE);
    if (em && !form.email) setForm((f) => ({ ...f, email: em[1] }));

    try {
      const resp = await fetch(`${API_URL}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current || undefined, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error(`Chat error (${resp.status})`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const lines = ev.split("\n");
          let evName = "", data = "";
          for (const l of lines) {
            if (l.startsWith("event:")) evName = l.slice(6).trim();
            else if (l.startsWith("data:")) data += l.slice(5).trim();
          }
          if (!data) continue;
          try {
            const payload = JSON.parse(data);
            if (evName === "session") {
              sessionIdRef.current = payload.session_id;
              try { localStorage.setItem(SESSION_KEY, payload.session_id); } catch {}
            } else if (payload.token) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: (copy[copy.length - 1]?.content || "") + payload.token,
                };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't reach the server. Try again in a moment." };
        return copy;
      });
    } finally {
      setBusy(false);
      // Auto-reveal capture form if we now have an email or after 3 exchanges
      if (!captured && (em || messages.filter((m) => m.role === "user").length >= 2)) {
        setCaptureOpen(true);
      }
    }
  };

  const submitCapture = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    if (!sessionIdRef.current) { toast.error("Please chat a little first."); return; }
    setCapBusy(true);
    try {
      const { data } = await api.post("/chat/capture", {
        session_id: sessionIdRef.current,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        country_code: form.country_code || null,
        country: form.country || null,
      });
      setCaptured(true);
      setCaptureOpen(false);
      if (data?.notification_sent === false) {
        toast.warning("Received — but the notification email couldn't be sent. Sanjeev will still see your details.");
      } else {
        toast.success("Thanks — Sanjeev has your details and will reach out soon.");
      }
      setMessages((m) => [...m, { role: "assistant", content: `Perfect, ${form.name} — Sanjeev has your details and will be in touch soon. Anything else I can help with meanwhile?` }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send. Please try again.");
    } finally { setCapBusy(false); }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-widget-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl hover:scale-105 transition grid place-items-center"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[600px] max-h-[85vh] rounded-2xl border border-border bg-background shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/50">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/20 text-primary grid place-items-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <div className="font-display text-sm leading-tight">Kai · Kairosa Guide</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Ask anything</div>
              </div>
            </div>
            <button
              data-testid="chat-widget-close"
              onClick={() => setOpen(false)}
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-foreground/10"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={"flex gap-2 " + (m.role === "user" ? "justify-end" : "justify-start")}>
                {m.role === "assistant" && (
                  <div className="h-7 w-7 rounded-full bg-primary/20 text-primary grid place-items-center shrink-0">
                    <Sparkles className="h-3.5 w-3.5" />
                  </div>
                )}
                <div
                  className={
                    "max-w-[75%] text-sm rounded-2xl px-3 py-2 whitespace-pre-wrap " +
                    (m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground")
                  }
                >
                  {m.content || (m.role === "assistant" && busy && i === messages.length - 1 ? "…" : "")}
                </div>
                {m.role === "user" && (
                  <div className="h-7 w-7 rounded-full bg-foreground/10 text-foreground grid place-items-center shrink-0">
                    <User className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            ))}

            {/* Inline capture form */}
            {captureOpen && !captured && (
              <form onSubmit={submitCapture} data-testid="chat-capture-form" className="mt-2 rounded-xl border border-primary/40 bg-primary/5 p-3 space-y-2">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-primary">
                  <Mail className="h-3.5 w-3.5" /> Share your details
                </div>
                <input
                  data-testid="chat-cap-name"
                  value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Your name *" required
                  className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                />
                <input
                  data-testid="chat-cap-email" type="email"
                  value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="Email *" required
                  className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                />
                <div className="grid grid-cols-[110px_1fr] gap-2">
                  <select
                    data-testid="chat-cap-code"
                    value={form.country_code} onChange={(e) => setForm((f) => ({ ...f, country_code: e.target.value }))}
                    className="h-9 rounded-md border border-border bg-background px-2 text-xs"
                  >
                    {DIAL_CODES.map(([c, l]) => <option key={c + l} value={c}>{c} · {l}</option>)}
                  </select>
                  <input
                    data-testid="chat-cap-phone"
                    value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="Phone number"
                    className="h-9 rounded-md border border-border bg-background px-3 text-sm"
                  />
                </div>
                <input
                  data-testid="chat-cap-country"
                  value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  placeholder="Country"
                  className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                />
                <div className="flex gap-2 justify-end pt-1">
                  <button type="button" onClick={() => setCaptureOpen(false)} className="text-xs px-3 py-1.5 rounded-full hover:bg-foreground/10">Not now</button>
                  <button
                    type="submit" disabled={capBusy}
                    data-testid="chat-cap-submit"
                    className="text-xs px-4 py-1.5 rounded-full bg-primary text-primary-foreground disabled:opacity-40"
                  >
                    {capBusy ? "Sending…" : "Send to founder"}
                  </button>
                </div>
              </form>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-border p-3 flex items-center gap-2"
          >
            <input
              data-testid="chat-widget-input"
              value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about plans, modules, integrations…"
              className="flex-1 h-10 rounded-full border border-border bg-background px-4 text-sm"
              disabled={busy}
            />
            <button
              data-testid="chat-widget-send" type="submit" disabled={busy || !input.trim()}
              className="h-10 w-10 grid place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-40"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>

          {!captured && !captureOpen && (
            <button
              onClick={() => setCaptureOpen(true)}
              className="text-[11px] text-primary underline underline-offset-2 pb-2 hover:text-primary/80"
              data-testid="chat-widget-share"
            >
              Share my details with the founder
            </button>
          )}
        </div>
      )}
    </>
  );
}
